import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Typography,
  Grid,
} from '@mui/material';
import { Circle as ColorIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type {
  ProjectWithRelations,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectStatus,
} from '../../types/project';
import { PROJECT_STATUS_OPTIONS, PROJECT_COLORS } from '../../types/project';

interface ProjectFormProps {
  project?: ProjectWithRelations | null;
  onChange: (data: CreateProjectRequest | UpdateProjectRequest) => void;
  clients?: Array<{ _id: string; firstName: string; lastName: string }>;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  onChange,
  clients = [],
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ProjectStatus>('planning');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [clientId, setClientId] = useState('');
  const [color, setColor] = useState<string | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Initialize form when project changes
  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description || '');
      setStatus(project.status);
      setStartDate(project.startDate ? new Date(project.startDate) : null);
      setDueDate(project.dueDate ? new Date(project.dueDate) : null);
      setClientId(project.clientId?._id || '');
      setColor(project.color);
      setTags(project.tags || []);
    } else {
      setName('');
      setDescription('');
      setStatus('planning');
      setStartDate(null);
      setDueDate(null);
      setClientId('');
      setColor(undefined);
      setTags([]);
    }
  }, [project]);

  // Emit changes
  useEffect(() => {
    onChange({
      name: name.trim(),
      description: description || undefined,
      status,
      startDate: startDate?.toISOString(),
      dueDate: dueDate?.toISOString(),
      clientId: clientId || undefined,
      color,
      tags,
    });
  }, [name, description, status, startDate, dueDate, clientId, color, tags]);

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <Stack spacing={3}>
      {/* Name */}
      <TextField
        label="Project Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
        placeholder="Enter project name"
      />

      {/* Description */}
      <TextField
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
        fullWidth
        placeholder="Describe the project..."
      />

      {/* Status */}
      <FormControl fullWidth>
        <InputLabel>Status</InputLabel>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as ProjectStatus)}
          label="Status"
        >
          {PROJECT_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Chip
                label={option.label}
                size="small"
                sx={{
                  backgroundColor: option.bgColor,
                  color: option.color,
                }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Dates */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            slotProps={{
              textField: { fullWidth: true },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Due Date"
            value={dueDate}
            onChange={(date) => setDueDate(date)}
            slotProps={{
              textField: { fullWidth: true },
            }}
          />
        </Grid>
      </Grid>

      {/* Client */}
      {clients.length > 0 && (
        <FormControl fullWidth>
          <InputLabel>Client (optional)</InputLabel>
          <Select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            label="Client (optional)"
          >
            <MenuItem value="">
              <em>No client</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client._id} value={client._id}>
                {client.firstName} {client.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Color Selection */}
      <Box>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Color (optional)
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => setColor(undefined)}
            sx={{
              border: !color ? '2px solid' : 'none',
              borderColor: 'primary.main',
            }}
          >
            <ColorIcon sx={{ color: 'grey.300' }} />
          </IconButton>
          {PROJECT_COLORS.map((c) => (
            <IconButton
              key={c}
              size="small"
              onClick={() => setColor(c)}
              sx={{
                border: color === c ? '2px solid' : 'none',
                borderColor: 'primary.main',
              }}
            >
              <ColorIcon sx={{ color: c }} />
            </IconButton>
          ))}
        </Stack>
      </Box>

      {/* Tags */}
      <Box>
        <TextField
          label="Add Tags"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown}
          onBlur={handleAddTag}
          fullWidth
          size="small"
          placeholder="Press Enter to add"
        />
        {tags.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onDelete={() => handleRemoveTag(tag)}
                size="small"
              />
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default ProjectForm;
